import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL, counterpartyApi } from '../services/api';
import ReportTable from '../components/ReportTable';
import ReportDownloadButtons from '../components/ReportDownloadButtons';

const CounterpartyReportPage = () => {
  const [filters, setFilters] = useState({
    counterparty: '',
    nicNumber: '',
    name: '',
    dateFrom: '',
    dateTo: ''
  });
  const [results, setResults] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Counterparty dropdown state
  const [counterpartyOptions, setCounterpartyOptions] = useState([]);
  const [counterpartyLoading, setCounterpartyLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/system-day`);
        if (!res.ok) return;
        const json = await res.json();
        const day = json.system_day ? String(json.system_day).split('T')[0] : '';
        if (day) {
          setFilters((f) => ({
            ...f,
            dateFrom: f.dateFrom || day,
            dateTo: f.dateTo || day
          }));
        }
      } catch (e) {
        // date inputs stay empty until the user picks them
      }
    })();
  }, []);

  useEffect(() => {
    async function fetchCounterparties() {
      setCounterpartyLoading(true);
      try {
        const data = await counterpartyApi.getAll();
        setCounterpartyOptions([
          { value: '', label: 'All Counterparties' },
          ...(data || []).map(cp => ({
            value: cp.unique_id,
            label: `${cp.short_name} (${cp.unique_id})`
          }))
        ]);
      } catch (e) {
        console.error('Error fetching counterparties:', e);
        setCounterpartyOptions([{ value: '', label: 'All Counterparties' }]);
      } finally {
        setCounterpartyLoading(false);
      }
    }
    fetchCounterparties();
  }, []);

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const buildParams = (pageState, extra = {}) => {
    const params = new URLSearchParams(extra);
    if (pageState) {
      params.set('page', String(pageState.page));
      params.set('pageSize', String(pageState.pageSize));
    }
    if (filters.counterparty) params.set('counterparty', filters.counterparty);
    if (filters.nicNumber) params.set('nicNumber', filters.nicNumber);
    if (filters.name) params.set('name', filters.name);
    if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.set('dateTo', filters.dateTo);
    return params;
  };

  const handleGenerateReport = async () => {
    if (!filters.dateFrom || !filters.dateTo) {
      setError('Please select Date From and Date To before generating the report.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const firstPage = { page: 1, pageSize: pagination.pageSize || 20 };
      const params = buildParams(firstPage);
      const res = await axios.get(`${API_BASE_URL}/reports/counterparty?${params.toString()}`);
      setResults(res.data.data || []);
      setPagination({ ...firstPage, total: res.data.total || 0 });
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Error fetching report');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = async (newPagination) => {
    setPagination(newPagination);
    setLoading(true);
    setError('');
    try {
      const params = buildParams(newPagination);
      const res = await axios.get(`${API_BASE_URL}/reports/counterparty?${params.toString()}`);
      setResults(res.data.data || []);
      setPagination(p => ({ ...newPagination, total: res.data.total || 0 }));
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Error fetching report');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (format) => {
    if (!filters.dateFrom || !filters.dateTo) {
      setError('Please select Date From and Date To before downloading the report.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const params = buildParams(null, { format });
      
      const res = await axios.get(`${API_BASE_URL}/reports/counterparty?${params.toString()}`, {
        responseType: 'blob'
      });
      
      // Create a link to download the file
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `counterparty_transaction_report.${format === 'excel' ? 'xlsx' : format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Error downloading report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="counterparty-report-page ui-page">
      <h2 style={{ marginBottom: '24px' }}>Counterparty Transaction Report</h2>
      
      {/* Filter Bar */}
      <div className="report-filter-bar" style={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: 16, 
        alignItems: 'flex-end', 
        marginBottom: 24,
        padding: '16px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #dee2e6'
      }}>
        {/* Counterparty Filter */}
        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Counterparty</label>
          <select
            value={filters.counterparty}
            onChange={(e) => handleFilterChange('counterparty', e.target.value)}
            disabled={counterpartyLoading}
            style={{
              padding: '8px',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              fontSize: '14px',
              minWidth: '250px',
              opacity: counterpartyLoading ? 0.6 : 1
            }}
          >
            {counterpartyLoading ? (
              <option value="">Loading...</option>
            ) : (
              counterpartyOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))
            )}
          </select>
        </div>
        
        {/* NIC Number Filter */}
        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>NIC Number</label>
          <input
            type="text"
            value={filters.nicNumber}
            onChange={(e) => handleFilterChange('nicNumber', e.target.value)}
            placeholder="Enter NIC number"
            style={{
              padding: '8px',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              fontSize: '14px',
              minWidth: '200px'
            }}
          />
        </div>
        
        {/* Name Filter */}
        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Name</label>
          <input
            type="text"
            value={filters.name}
            onChange={(e) => handleFilterChange('name', e.target.value)}
            placeholder="Enter name"
            style={{
              padding: '8px',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              fontSize: '14px',
              minWidth: '200px'
            }}
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Date From *</label>
          <input
            type="date"
            value={filters.dateFrom}
            onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
            style={{
              padding: '8px',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Date To *</label>
          <input
            type="date"
            value={filters.dateTo}
            onChange={(e) => handleFilterChange('dateTo', e.target.value)}
            style={{
              padding: '8px',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          />
        </div>
        
        {/* Generate Button */}
        <div>
          <button 
            type="button" 
            onClick={handleGenerateReport}
            disabled={loading || !filters.dateFrom || !filters.dateTo}
            style={{ 
              padding: '8px 16px', 
              background: '#007bff', 
              color: '#fff', 
              border: 'none', 
              borderRadius: 4,
              cursor: (loading || !filters.dateFrom || !filters.dateTo) ? 'not-allowed' : 'pointer',
              opacity: (loading || !filters.dateFrom || !filters.dateTo) ? 0.6 : 1
            }}
          >
            {loading ? 'Generating...' : 'Generate Report'}
          </button>
        </div>
      </div>
      
      {error && (
        <div style={{ 
          color: 'red', 
          padding: '12px', 
          marginBottom: '16px',
          backgroundColor: '#fee',
          border: '1px solid #fcc',
          borderRadius: '4px'
        }}>
          Error: {error}
        </div>
      )}
      
      <ReportDownloadButtons 
        onDownload={handleDownload} 
        disabled={loading || results.length === 0} 
      />
      
      <ReportTable
        data={results}
        pagination={pagination}
        loading={loading}
        error={error}
        onPageChange={handlePageChange}
        reportType="counterparty"
      />
    </div>
  );
};

export default CounterpartyReportPage;

